import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Store,
  Palette,
  Layout,
  Layers,
  Eye,
  Plus,
  Check,
  Sparkles,
  ArrowRight,
  Crown,
  ShoppingBag,
  Heart,
  Pill,
  Shirt,
  Home as HomeIcon,
  Utensils,
  Grid3X3 } from
'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const CATEGORY_ICONS = {
  beauty: Heart,
  fashion: Shirt,
  health: Pill,
  home: HomeIcon,
  food: Utensils,
  general: Grid3X3
};

export const StorefrontBuilder = () => {const { t } = useTranslation("ecommerce");
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [storeName, setStoreName] = useState('');
  const [storeSlug, setStoreSlug] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ecommerce/templates`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
        setCategories(data.categories || []);
      }
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setShowCreateModal(true);
  };

  const handleCreateStore = async () => {
    if (!storeName || !storeSlug) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/ecommerce/stores`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: storeName,
          slug: storeSlug.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
          template_id: selectedTemplate?.id
        })
      });

      if (res.ok) {
        toast.success('Store created successfully!');
        setShowCreateModal(false);
        setStoreName('');
        setStoreSlug('');
        // Navigate to store settings
        window.location.href = `/ecommerce/stores`;
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create store');
      }
    } catch (err) {
      toast.error('Error creating store');
    }
  };

  const filteredTemplates = selectedCategory === 'all' ?
  templates :
  templates.filter((t) => t.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="storefront-builder">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-8 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Store className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold font-['Space_Grotesk']">{t("storefrontBuilder", "Storefront Builder")}</h1>
              <p className="text-emerald-100 mt-1">{t("createStunningOnline", "Create stunning online stores with professional templates")}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mt-6">
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 backdrop-blur-sm">
              <Palette className="h-4 w-4" />
              <span className="text-sm">{t("5PremiumTemplates", "5+ Premium Templates")}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 backdrop-blur-sm">
              <Layout className="h-4 w-4" />
              <span className="text-sm">{t("dragDropBuilder", "Drag & Drop Builder")}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 backdrop-blur-sm">
              <Layers className="h-4 w-4" />
              <span className="text-sm">{t("mobileResponsive", "Mobile Responsive")}</span>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute -top-10 -right-20 w-60 h-60 bg-cyan-400/20 rounded-full blur-3xl" />
      </div>

      {/* Category Filter */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
        <TabsList className="bg-muted/50 p-1 flex-wrap h-auto gap-1">
          <TabsTrigger value="all" className="gap-2">
            <Grid3X3 className="h-4 w-4" />{t("allTemplates", "All Templates")}

          </TabsTrigger>
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat] || Grid3X3;
            return (
              <TabsTrigger key={cat} value={cat} className="gap-2 capitalize">
                <Icon className="h-4 w-4" />
                {cat}
              </TabsTrigger>);

          })}
        </TabsList>
      </Tabs>

      {/* Templates Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredTemplates.map((template) =>
        <Card
          key={template.id}
          className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
          onClick={() => handleSelectTemplate(template)}>
          
            {/* Template Preview Image */}
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
              src={template.thumbnail_url}
              alt={template.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              {/* Overlay Actions */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Button className="gap-2 bg-white text-gray-900 hover:bg-white/90 shadow-xl">
                  <Eye className="h-4 w-4" />{t("preview", "Preview")}

              </Button>
              </div>

              {/* Premium Badge */}
              {template.is_premium &&
            <div className="absolute top-3 right-3">
                  <Badge className="gap-1 bg-gradient-to-r from-amber-500 to-orange-500 border-0 text-white shadow-lg">
                    <Crown className="h-3 w-3" />{t("premium", "Premium")}

              </Badge>
                </div>
            }

              {/* Category Badge */}
              <div className="absolute top-3 left-3">
                <Badge variant="secondary" className="capitalize bg-white/90 backdrop-blur-sm">
                  {template.category}
                </Badge>
              </div>
            </div>

            {/* Template Info */}
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-lg group-hover:text-emerald-600 transition-colors">
                    {template.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {template.description}
                  </p>
                </div>
              </div>

              {/* Color Preview */}
              <div className="flex items-center gap-2 mt-4">
                <span className="text-xs text-muted-foreground">{t("theme", "Theme:")}</span>
                <div className="flex gap-1">
                  <div
                  className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: template.default_theme?.primary_color }}
                  title="Primary" />
                
                  <div
                  className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: template.default_theme?.secondary_color }}
                  title="Secondary" />
                
                  <div
                  className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: template.default_theme?.accent_color }}
                  title="Accent" />
                
                </div>
              </div>

              {/* Features */}
              <div className="flex flex-wrap gap-1.5 mt-4">
                {template.features?.slice(0, 3).map((feature, idx) =>
              <Badge key={idx} variant="outline" className="text-xs capitalize">
                    {feature.replace(/_/g, ' ')}
                  </Badge>
              )}
                {template.features?.length > 3 &&
              <Badge variant="outline" className="text-xs">
                    +{template.features.length - 3}{t("more", "more")}
              </Badge>
              }
              </div>

              {/* Select Button */}
              <Button
              className="w-full mt-4 gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              onClick={(e) => {e.stopPropagation();handleSelectTemplate(template);}}>
              
                <Sparkles className="h-4 w-4" />{t("useThisTemplate", "Use This Template")}

              <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Store Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="h-5 w-5 text-emerald-600" />{t("createYourStore", "Create Your Store")}

            </DialogTitle>
          </DialogHeader>
          
          {selectedTemplate &&
          <div className="space-y-6 py-4">
              {/* Selected Template Preview */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                <img
                src={selectedTemplate.thumbnail_url}
                alt={selectedTemplate.name}
                className="w-20 h-16 object-cover rounded-lg" />
              
                <div>
                  <p className="font-medium">{selectedTemplate.name}</p>
                  <p className="text-sm text-muted-foreground">{t("templateSelected", "Template selected")}</p>
                </div>
                <Check className="ml-auto h-5 w-5 text-emerald-600" />
              </div>

              {/* Store Name */}
              <div>
                <label className="text-sm font-medium">{t("storeName", "Store Name")}</label>
                <input
                type="text"
                value={storeName}
                onChange={(e) => {
                  setStoreName(e.target.value);
                  setStoreSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
                }}
                placeholder="My Amazing Store"
                className="w-full mt-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              
              </div>

              {/* Store URL */}
              <div>
                <label className="text-sm font-medium">{t("storeUrl", "Store URL")}</label>
                <div className="flex items-center mt-1">
                  <span className="px-3 py-2 bg-muted text-muted-foreground text-sm rounded-l-lg border border-r-0">{t("store", "/store/")}

                </span>
                  <input
                  type="text"
                  value={storeSlug}
                  onChange={(e) => setStoreSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="my-store"
                  className="flex-1 px-4 py-2 border rounded-r-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t("yourStoreWill", "Your store will be available at: /store/")}
                {storeSlug || 'your-store'}
                </p>
              </div>
            </div>
          }

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              onClick={handleCreateStore}
              className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
              
              <Plus className="h-4 w-4" />{t("createStore", "Create Store")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default StorefrontBuilder;